import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  PersonalSetting,
  ViewIds,
  type APIReferenceType,
} from '@ardoq/api-types';
import { RelationshipsViewOwnProps } from './types';
import { getLeftMenu } from './menus';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { getExportsForSvgView } from '@ardoq/export';
import { getExportLabels } from 'views/getExportLabels';
import { Team } from '@ardoq/profiling';
import { usePerformanceTracking } from 'tabview/graphComponent/usePerformanceTracking';
import { subscribeToAction } from 'streams/utils/streamUtils';
import { getViewModel$ } from './viewModel$';
import { ZoomControls } from 'atomicComponents/Zoomable/ZoomControls';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import type { ErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import type { I18nLocale } from '@ardoq/i18n';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import {
  getActiveConditionalFormattingForLegend,
  getComponentTypesForLegend,
  viewLegendCommands,
} from '@ardoq/view-legend';
import {
  type Zoom,
  fitWindow,
  fitWindowMargin,
  selectSvgTransition,
  type SetNumeric,
  RELATIONSHIPS_2_ANIMATION_THRESHOLD,
  RELATIONSHIPS_2_MAX_ITERATIONS,
  type RelationshipsViewStreamedProps,
  type RelationshipsViewSettings,
  initializeForce,
  RelationshipsVisualization,
} from '@ardoq/graph';
import { WithLoadingIndicator } from 'tabview/WithLoadingIndicator';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { dispatchAction } from '@ardoq/rxbeach';
import { RelationshipsViewLegend } from './RelationshipsViewLegend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { RelationshipsViewSettingsBar } from './RelationshipsViewSettingsBar';
import { toggleLegend } from 'presentation/viewPane/actions';
import { notifyViewLoading } from 'tabview/actions';
import { noop } from 'lodash';
import { ZoomContainer } from 'tabview/relationshipDiagrams/atoms';
import { onViewSettingsUpdate as ardoqFrontOnViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { getActiveDiffMode } from 'scope/activeDiffMode$';
import { isInScopeDiffMode } from 'scope/scopeDiff';
import { useViewLegendSubscription } from 'views/viewLegend/useViewLegendSubscription';
import { ViewAndLegendContainer } from 'tabview/ViewAndLegendContainer';
import { navigateToComponent } from 'tabview/navigateToComponent';
import { getSharedExportFunctions } from 'tabview/getSharedExportFunctions';
import { isPresentationMode } from 'env';
import { setIsCurrentlyHoveredImageBroken } from 'tabview/graphComponent/isCurrentlyHoveredImageBroken$';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { EmptyState as EmptyStateBase } from './hierarchical/EmptyState';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.NETWORK;

const Container = styled.div`
  user-select: none;
  & text {
    pointer-events: none;
  }
`;

const iterationsByGraphItems = new Map([
  [RELATIONSHIPS_2_ANIMATION_THRESHOLD, 0],
  [500, 200],
  [250, 250],
  [0, RELATIONSHIPS_2_MAX_ITERATIONS],
]);
const graphItemsThresholds = [...iterationsByGraphItems.keys()];
const getIterations = (graphItemsCount: number) =>
  iterationsByGraphItems.get(
    graphItemsThresholds.find(key => key < graphItemsCount) ?? 0
  ) ?? RELATIONSHIPS_2_MAX_ITERATIONS;

type StreamedProps =
  RelationshipsViewStreamedProps<RelationshipsViewSettings> & {
    errorInfoBoxLabels: ErrorInfoBoxLabels;
    i18nLocale: I18nLocale;
    quickPerspectiveReferenceTypeFilters: Set<string> | null;
    referenceTypesForLegend: APIReferenceType[];
  };
const RelationshipsView = ({
  viewSettings,
  viewModel: {
    graphNodes,
    graphGroups,
    groupChildren,
    graphLinks,
    forceLinks,
    maxGroupDepth,
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
    referenceTypes,
    errors,
    hasClones,
    noConnectedComponents,
  },
  viewInstanceId,
  onViewLoading = noop,
  onToggleLegend = noop,
  onViewSettingsUpdate = noop,
  toggleNeverShowAgain = noop,
  neverShowAgain = false,
  activeDiffMode,
  heightOffset,
  selectedComponentId,
  errorInfoBoxLabels,
  i18nLocale,
  quickPerspectiveReferenceTypeFilters,
  referenceTypesForLegend,
}: StreamedProps & RelationshipsViewOwnProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [zoom, setZoom] = useState<Zoom | null>(null);
  const [updateNodeDistance, setUpdateNodeDistance] = useState<SetNumeric>(
    () => {}
  );
  const [isInitialized, setIsInitialized] = useState(false);

  const { nodeDistance, animate, isLegendActive } = viewSettings;
  const graphItemsCount = graphNodes.length + graphLinks.length;
  const idealIterations = getIterations(graphItemsCount);
  const iterations = animate ? idealIterations : 0;
  const isViewEmpty = !graphNodes.length || noConnectedComponents;

  useEffect(() => {
    const {
      zoom: newZoom,
      setNodeDistance,
      cleanupForce,
    } = initializeForce({
      container: containerRef.current,
      nodeDistance,
      maxGroupDepth,
      graphNodes,
      forceLinks,
      graphLinks,
      graphGroups,
      groupChildren,
      iterations,
      viewInstanceId,
      onViewLoading,
      selectedEntityId: selectedComponentId,
    });
    setZoom(() => newZoom);
    setUpdateNodeDistance(() => setNodeDistance);
    setIsInitialized(true);

    const toggleLegendSubscription = subscribeToAction(
      toggleLegend,
      ({ legendActive }) => {
        legend.current?.setIsVisible(legendActive);
        settingsBar.current?.setIsLegendActive(legendActive);
      },
      VIEW_ID
    );
    return () => {
      cleanupForce();
      toggleLegendSubscription.unsubscribe();
    };
  }, [
    graphNodes,
    graphLinks,
    graphGroups,
    groupChildren,
    forceLinks,
    maxGroupDepth,
    nodeDistance,
    iterations,
    viewInstanceId,
    onViewLoading,
    selectedComponentId,
  ]);

  const [clearedErrors, setClearedErrors] = useState(false);

  const [clearedHasClones, setClearedHasClones] = useState(false);
  const settingsBar =
    useRef<React.ElementRef<typeof RelationshipsViewSettingsBar>>(null);
  const legend = useRef<React.ElementRef<typeof RelationshipsViewLegend>>(null);
  return (
    <Container
      className={`tab-pane ${VIEW_ID}Tab active`}
      onContextMenu={e => e.preventDefault()}
      data-context-menu={OBJECT_CONTEXT_MENU_NAME}
    >
      <div className="menuContainer">
        <RelationshipsViewSettingsBar
          ref={settingsBar}
          viewId={VIEW_ID}
          i18nLocale={i18nLocale}
          leftMenu={getLeftMenu({
            viewSettings,
            traversedOutgoingReferenceTypes,
            traversedIncomingReferenceTypes,
            referenceTypes,
            quickPerspectiveReferenceTypeFilters,
            canAnimate: idealIterations > 0,
            onNodeDistanceUpdated: updateNodeDistance,
            onViewSettingsUpdate,
            viewId: VIEW_ID,
            i18nLocale,
          })}
          rightMenu={getRightMenuConfig({
            isViewpointMode: false,
            viewId: VIEW_ID,
            viewstate: viewSettings,
            exports: getExportsForSvgView({
              container: () => containerRef.current,
              exportedViewMetadata: {
                name: VIEW_ID,
                isViewpointMode: false,
              },
              ...getSharedExportFunctions(),
              labels: getExportLabels(i18nLocale),
            }),
            knowledgeBaseLink: KnowledgeBaseLink.RELATIONSHIPS,
            legendOnClick: () => {
              viewLegendCommands.updateViewLegendSettings({
                viewId: VIEW_ID,
                isActive: !isLegendActive,
              });
              onToggleLegend(!isLegendActive);
            },
            onViewSettingsUpdate,
            i18nLocale,
          })}
          isLegendInitiallyActive={isLegendActive}
        />
      </div>
      <ViewAndLegendContainer ref={containerRef}>
        {isViewEmpty ? (
          <EmptyStateBase
            noConnectedComponents={noConnectedComponents}
            viewId={ViewIds.NETWORK}
          />
        ) : (
          <>
            <ZoomContainer>
              <ZoomControls
                zoomIn={() =>
                  selectSvgTransition(containerRef.current).call(
                    zoom!.scaleBy,
                    1.2
                  )
                }
                zoomOut={() =>
                  selectSvgTransition(containerRef.current).call(
                    zoom!.scaleBy,
                    Math.pow(1.2, -1)
                  )
                }
                zoomCenter={() =>
                  fitWindow({
                    container: containerRef.current!,
                    margin: fitWindowMargin(maxGroupDepth),
                    zoom: zoom!,
                  })
                }
                i18nLocale={i18nLocale}
              />
            </ZoomContainer>
            <RelationshipsVisualization
              isInitialized={isInitialized}
              graphNodes={graphNodes}
              graphGroups={graphGroups}
              graphLinks={graphLinks}
              onComponentDoubleClick={navigateToComponent}
              selectedEntityId={selectedComponentId}
              onBrokenImageMouseEnter={imageElement =>
                dispatchAction(
                  setIsCurrentlyHoveredImageBroken({
                    isCurrentlyHoveredImageBroken: true,
                    imageElement: imageElement,
                  })
                )
              }
              onBrokenImageMouseLeave={imageElement =>
                dispatchAction(
                  setIsCurrentlyHoveredImageBroken({
                    isCurrentlyHoveredImageBroken: false,
                    imageElement: imageElement,
                  })
                )
              }
            />
            <ErrorInfoBox
              errors={clearedErrors ? [] : errors}
              hasClones={!neverShowAgain && !clearedHasClones && hasClones}
              clearHasClones={() => setClearedHasClones(true)}
              clearErrors={() => setClearedErrors(true)}
              isShowNeverAgainSet={neverShowAgain}
              isPresentationMode={isPresentationMode()}
              toggleNeverShowAgain={toggleNeverShowAgain}
              {...errorInfoBoxLabels}
            />
            <RelationshipsViewLegend
              initiallyVisible={isLegendActive}
              ref={legend}
              componentTypes={getComponentTypesForLegend(
                [...graphNodes, ...graphGroups].map(node => node.modelId)
              )}
              referenceTypes={referenceTypesForLegend}
              activeConditionalFormatting={getActiveConditionalFormattingForLegend(
                i18nLocale
              )}
              activeDiffMode={activeDiffMode}
              heightOffset={heightOffset}
              labels={getViewLegendLabels(i18nLocale)}
            />
          </>
        )}
      </ViewAndLegendContainer>
    </Container>
  );
};

const RelationshipsViewWithSideEffects = (props: StreamedProps) => {
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_NETWORK
  );

  const activeDiffMode = getActiveDiffMode();
  const isScopeDiffMode = isInScopeDiffMode();

  return (
    <RelationshipsView
      {...props}
      toggleNeverShowAgain={toggleNeverShowAgain}
      neverShowAgain={neverShowAgain}
      onViewLoading={(isBusy, viewInstanceId) => {
        dispatchAction(notifyViewLoading(viewInstanceId, { isBusy }));
      }}
      onToggleLegend={isLegendActive => {
        dispatchAction(
          toggleLegend({ viewId: VIEW_ID, legendActive: isLegendActive }),
          VIEW_ID
        );
      }}
      onViewSettingsUpdate={(_, settings, persistent) =>
        ardoqFrontOnViewSettingsUpdate(VIEW_ID, settings, persistent)
      }
      activeDiffMode={isScopeDiffMode ? activeDiffMode : null}
      heightOffset={useViewLegendSubscription()}
    />
  );
};

const PerformanceTrackedRelationshipsView = (props: StreamedProps) => {
  usePerformanceTracking('relationships view render', 2000, {
    viewId: VIEW_ID,
    team: Team.INSIGHT,
    metadata: {
      graphNodesCount: props.viewModel.graphNodes.length,
      graphGroupsCount: props.viewModel.graphGroups.length,
      graphLinks: props.viewModel.graphLinks.length,
      maxGroupDepth: props.viewModel.maxGroupDepth,
    },
  });

  return <RelationshipsViewWithSideEffects {...props} />;
};

const RelationshipsViewWithLoadingIndicator = (props: StreamedProps) => (
  <WithLoadingIndicator
    viewInstanceId={props.viewInstanceId}
    loadingSource="notifyViewLoading"
    showContentWhileLoading
    i18nLocale={props.i18nLocale}
  >
    <PerformanceTrackedRelationshipsView {...props} />
  </WithLoadingIndicator>
);

const ConnectedRelationshipsView = connectInstanceWithProps(
  RelationshipsViewWithLoadingIndicator,
  getViewModel$
);

export { ConnectedRelationshipsView };
